import type { JobRow, JobStatus } from '../types/jobs';
import { supabase } from './supabase';
import { toMediaUrl } from './mediaUrl';
import { notifyVideoReadyEmail } from './notifyVideoReady';

export { toMediaUrl } from './mediaUrl';

/** Status/result columns only — `input` can be multi-MB (frame b64) and breaks Realtime payloads. */
const JOB_STATUS_COLUMNS =
  'id, org_id, user_id, generation_id, media_type, modality, provider, model, status, provider_task_id, result_url, error_message, credits_charged, created_at, updated_at, completed_at';

export async function fetchJob(jobId: string): Promise<JobRow | null> {
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_STATUS_COLUMNS)
    .eq('id', jobId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as JobRow | null;
}

export async function listActiveJobs(orgId: string, limit = 20): Promise<JobRow[]> {
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_STATUS_COLUMNS)
    .eq('org_id', orgId)
    .in('status', ['pending', 'queued', 'submitted', 'processing'])
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data || []) as JobRow[];
}

export async function listRecentCompletedJobs(orgId: string, sinceIso: string, limit = 10): Promise<JobRow[]> {
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_STATUS_COLUMNS)
    .eq('org_id', orgId)
    .eq('status', 'completed')
    .gte('completed_at', sinceIso)
    .order('completed_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data || []) as JobRow[];
}

const VIDEO_JOB_LS = 'seu.video.creationVideoJob';
const VIDEO_JOB_DISMISSED_LS = 'seu.video.dismissedVideoJobs';
const VIDEO_JOB_MAX_AGE_MS = 2 * 60 * 60 * 1000;

export function rememberCreationVideoJob(jobId: string) {
  try {
    localStorage.setItem(VIDEO_JOB_LS, JSON.stringify({ id: jobId, at: Date.now() }));
  } catch {
    /* private mode */
  }
}

const SUPPRESS_TOAST_LS = 'seu.video.suppressVideoJobToasts';

/** Skip global “Vídeo pronto” toast for intermediate multiclip parts. */
export function suppressVideoJobCompletionToast(jobId: string) {
  if (!jobId) return;
  try {
    const raw = sessionStorage.getItem(SUPPRESS_TOAST_LS);
    const list: string[] = raw ? (JSON.parse(raw) as string[]) : [];
    if (!list.includes(jobId)) {
      list.push(jobId);
      // Keep bounded; multiclip sessions are small.
      sessionStorage.setItem(SUPPRESS_TOAST_LS, JSON.stringify(list.slice(-40)));
    }
  } catch {
    /* ignore */
  }
}

export function shouldSuppressVideoJobCompletionToast(jobId: string | null | undefined): boolean {
  if (!jobId) return false;
  try {
    const raw = sessionStorage.getItem(SUPPRESS_TOAST_LS);
    if (!raw) return false;
    const list = JSON.parse(raw) as string[];
    return Array.isArray(list) && list.includes(jobId);
  } catch {
    return false;
  }
}

export function readCreationVideoJob(): string | null {
  try {
    const raw = localStorage.getItem(VIDEO_JOB_LS);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string; at?: number };
    if (!parsed?.id || !parsed.at || Date.now() - parsed.at > VIDEO_JOB_MAX_AGE_MS) {
      localStorage.removeItem(VIDEO_JOB_LS);
      return null;
    }
    return parsed.id;
  } catch {
    return null;
  }
}

export function clearCreationVideoJob() {
  try {
    localStorage.removeItem(VIDEO_JOB_LS);
  } catch {
    /* ignore */
  }
}

function readDismissedVideoJobs(): Set<string> {
  try {
    const raw = sessionStorage.getItem(VIDEO_JOB_DISMISSED_LS);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as string[];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

export function dismissVideoJob(jobId: string) {
  try {
    const set = readDismissedVideoJobs();
    set.add(jobId);
    sessionStorage.setItem(VIDEO_JOB_DISMISSED_LS, JSON.stringify(Array.from(set)));
  } catch {
    /* ignore */
  }
  const stored = readCreationVideoJob();
  if (stored === jobId) clearCreationVideoJob();
}

export function isVideoJobDismissed(jobId: string): boolean {
  return readDismissedVideoJobs().has(jobId);
}

const IN_FLIGHT: JobStatus[] = ['pending', 'queued', 'submitted', 'processing'];

/** Active video jobs for the slim progress bar (skips session-dismissed). */
export async function listActiveVideoJobs(
  orgId: string,
  opts?: { limit?: number; userId?: string | null },
): Promise<JobRow[]> {
  const limit = opts?.limit ?? 10;
  let q = supabase
    .from('jobs')
    .select(JOB_STATUS_COLUMNS)
    .eq('org_id', orgId)
    .eq('media_type', 'video')
    .in('status', ['pending', 'queued', 'submitted', 'processing'])
    .order('created_at', { ascending: false })
    .limit(limit);
  if (opts?.userId) q = q.eq('user_id', opts.userId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return ((data || []) as JobRow[]).filter((j) => !isVideoJobDismissed(j.id));
}

/**
 * @deprecated Prefer listActiveVideoJobs + non-blocking progress bar.
 * Only returns the explicitly remembered job (no org-wide fallback).
 */
export async function findResumableVideoJob(orgId: string): Promise<JobRow | null> {
  const storedId = readCreationVideoJob();
  if (!storedId || isVideoJobDismissed(storedId)) {
    if (storedId) clearCreationVideoJob();
    return null;
  }
  const stored = await fetchJob(storedId);
  if (stored?.media_type === 'video' && IN_FLIGHT.includes(stored.status)) return stored;
  clearCreationVideoJob();
  void orgId;
  return null;
}

const TERMINAL: JobStatus[] = ['completed', 'failed'];

/**
 * Wait until job reaches completed/failed using Realtime + hydrate fallback.
 * Survives brief disconnects via periodic select.
 */
export function waitForJob(
  jobId: string,
  options?: {
    timeoutMs?: number;
    onStatus?: (job: JobRow) => void;
    signal?: AbortSignal;
  },
): Promise<JobRow> {
  const timeoutMs = options?.timeoutMs ?? 15 * 60 * 1000;

  return new Promise((resolve, reject) => {
    let settled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let timeoutTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (pollTimer) clearInterval(pollTimer);
      if (timeoutTimer) clearTimeout(timeoutTimer);
      if (channel) {
        void supabase.removeChannel(channel);
        channel = null;
      }
    };

    const finish = (job: JobRow) => {
      if (settled) return;
      if (!TERMINAL.includes(job.status)) return;
      settled = true;
      cleanup();
      if (job.status === 'failed') {
        reject(new Error(job.error_message || 'Job failed'));
      } else {
        if (job.media_type === 'video' && job.status === 'completed') {
          notifyVideoReadyEmail(job.id);
        }
        resolve(job);
      }
    };

    const fail = (err: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    };

    if (options?.signal) {
      if (options.signal.aborted) {
        fail(new Error('Aborted'));
        return;
      }
      options.signal.addEventListener('abort', () => fail(new Error('Aborted')), { once: true });
    }

    const hydrate = async () => {
      try {
        const job = await fetchJob(jobId);
        if (!job) return;
        options?.onStatus?.(job);
        finish(job);
      } catch (err: any) {
        /* keep waiting — transient */
        console.warn('waitForJob hydrate', err?.message || err);
      }
    };

    channel = supabase
      .channel(`job-${jobId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'jobs',
          filter: `id=eq.${jobId}`,
        },
        () => {
          // Don't trust payload.new: jobs.input with frame b64 exceeds Realtime size
          // and arrives as status=completed with a missing result_url.
          void hydrate();
        },
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') void hydrate();
      });

    // Hydrate periodically in case Realtime misses an event
    pollTimer = setInterval(() => void hydrate(), 8000);
    timeoutTimer = setTimeout(() => fail(new Error('Tempo limite aguardando o job.')), timeoutMs);

    void hydrate();
  });
}

export function jobIdsFromAsyncResponse(data: {
  job_id?: string;
  job_ids?: string[];
} | null | undefined): string[] {
  if (Array.isArray(data?.job_ids) && data.job_ids.length) return data.job_ids;
  if (data?.job_id) return [data.job_id];
  return [];
}

export function jobStatusLabel(j: JobRow): string {
  if (j.status === 'completed') return 'Pronto';
  if (j.status === 'processing') return 'Gerando...';
  if (j.status === 'submitted' || j.status === 'queued') return 'Na fila — acompanhe o progresso';
  if (j.status === 'failed') return j.error_message || 'Falha na geração';
  if (j.status === 'pending') return 'Na fila — acompanhe o progresso';
  return 'Preparando...';
}

export async function waitForJobResultUrl(
  jobId: string,
  options?: {
    timeoutMs?: number;
    onStatus?: (job: JobRow) => void;
  },
): Promise<string> {
  const job = await waitForJob(jobId, options);
  const url = toMediaUrl(job.result_url);
  if (!url) throw new Error('Job concluído sem result_url');
  return url;
}

/** PoYo wan image under a 7-stage batch often takes 20–50 min wall-clock. */
export const STAGE_JOBS_TIMEOUT_MS = 60 * 60 * 1000;

/**
 * Wait for many stage image jobs. Shows completed URLs as they finish.
 * Does not fail the whole batch when one job fails — caller decides.
 */
export async function waitForStageJobs(
  jobIds: string[],
  options?: {
    timeoutMs?: number;
    /** Sparse by job index — holes stay null so Stage N never receives Stage N+1's URL. */
    onPartial?: (urls: Array<string | null>) => void;
  },
): Promise<{ urls: string[]; failed: number; jobs: JobRow[] }> {
  const timeoutMs = options?.timeoutMs ?? STAGE_JOBS_TIMEOUT_MS;
  const byIndex: Array<string | null> = jobIds.map(() => null);
  const jobs: JobRow[] = [];
  let failed = 0;

  const publish = () => {
    options?.onPartial?.([...byIndex]);
  };

  await Promise.all(
    jobIds.map(async (id, index) => {
      try {
        const job = await waitForJob(id, { timeoutMs });
        jobs[index] = job;
        const url = toMediaUrl(job.result_url);
        if (url) {
          byIndex[index] = url;
          publish();
        } else {
          failed += 1;
        }
      } catch {
        // Last chance: job may have completed just as the wait timed out.
        try {
          const late = await fetchJob(id);
          if (late) {
            jobs[index] = late;
            const url = toMediaUrl(late.result_url);
            if (late.status === 'completed' && url) {
              byIndex[index] = url;
              publish();
              return;
            }
          }
        } catch {
          /* ignore */
        }
        failed += 1;
      }
    }),
  );

  return {
    urls: byIndex.filter((u): u is string => Boolean(u)),
    failed,
    jobs: jobs.filter(Boolean),
  };
}

export async function waitForPreviewJobs(
  data: { job_id?: string; job_ids?: string[]; generation_id?: string },
  onStatus?: (job: JobRow) => void,
): Promise<{
  generation_id?: string;
  previews: Array<{ id: string; label: string; image_b64: string; prompt: string }>;
}> {
  const ids = jobIdsFromAsyncResponse(data);
  if (!ids.length) throw new Error('Resposta async sem job_id');
  const jobs = await Promise.all(ids.map((id) => waitForJob(id, { onStatus })));
  return {
    generation_id: data.generation_id,
    previews: jobs.map((j, i) => {
      const url = toMediaUrl(j.result_url);
      if (!url) throw new Error('Job concluído sem result_url');
      return {
        id: j.id,
        label: `Opção ${i + 1}`,
        image_b64: url,
        prompt: '',
      };
    }),
  };
}
